/**
 * Step 5: Validation - Récapitulatif avec modification rapide inline
 * Permet de modifier chaque section sans revenir aux étapes précédentes
 * Le prix se recalcule dynamiquement
 * Inclut le choix du mode de paiement (carte ou cash)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import { bookingAPI, pricingAPI } from '../../services/bookingApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import { 
  MapPin, Calendar, Clock, Users, Briefcase, Car, 
  User, Mail, Phone, AlertCircle, Loader2,
  ArrowLeft, CreditCard, Pencil, Plane, Train, Tag,
  Check, X, Plus, Minus, Save, Banknote, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

const Step6Confirmation = () => {
  const navigate = useNavigate();
  const { bookingData, updateBookingData, prevStep, nextStep } = useBooking();
  const { user } = useAuth();
  
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'cash'
  
  // Modes d'édition inline
  const [editMode, setEditMode] = useState(null); // 'trip' | 'passengers' | 'contact' | null
  const [editData, setEditData] = useState({});
  const [recalculating, setRecalculating] = useState(false);
  
  // Options et véhicules
  const [vehicles, setVehicles] = useState([]);
  const [options, setOptions] = useState([]);
  
  // Charger options et véhicules
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/bookings/options`);
        setVehicles(response.data.vehicle_categories || []);
        setOptions(response.data.options || []);
      } catch (err) {
        console.error('Error loading options:', err);
      }
    };
    loadData();
  }, []);

  const vehicleNames = vehicles.reduce((acc, v) => {
    acc[v.category] = v.name;
    return acc;
  }, {
    berline: 'Berline',
    suv: 'SUV Premium',
    van: 'Van',
    minibus: 'Minibus'
  });

  const optionNames = options.reduce((acc, o) => {
    acc[o.value] = o.name;
    return acc;
  }, {
    child_seat: 'Siège bébé',
    welcome_sign: "Panneau d'accueil",
    water_bottle: "Bouteille d'eau",
    extra_wait: "Attente supplémentaire"
  });

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Recalculer le prix
  const recalculatePrice = useCallback(async (newData = {}) => {
    const data = { ...bookingData, ...newData };
    
    if (!data.pickup?.latitude || !data.dropoff?.latitude) return;
    
    setRecalculating(true);
    try {
      const response = await pricingAPI.calculatePrice({
        pickup_address: data.pickup.address,
        pickup_lat: data.pickup.latitude,
        pickup_lng: data.pickup.longitude,
        dropoff_address: data.dropoff.address,
        dropoff_lat: data.dropoff.latitude,
        dropoff_lng: data.dropoff.longitude,
        pickup_datetime: data.pickup_datetime || new Date().toISOString(),
        vehicle_category: data.vehicle_category,
        service_type: 'transfer',
        passengers: data.passengers || 1,
        luggage: data.luggage || 0,
        is_round_trip: data.is_round_trip || false,
        intermediate_stops: (data.intermediate_stops || []).map(stop => ({
          address: stop.address,
          latitude: stop.latitude,
          longitude: stop.longitude
        }))
      });
      
      updateBookingData({
        ...newData,
        price_details: response.price_details,
        price_estimate: { total: response.price_details?.total_ttc || 0 }
      });
    } catch (err) {
      console.error('Price recalculation error:', err);
    } finally {
      setRecalculating(false);
    }
  }, [bookingData, updateBookingData]);

  // Démarrer l'édition
  const startEdit = (mode) => {
    setEditMode(mode);
    if (mode === 'trip') {
      setEditData({
        pickup: bookingData.pickup,
        dropoff: bookingData.dropoff,
        pickup_datetime: bookingData.pickup_datetime,
        is_round_trip: bookingData.is_round_trip,
        return_datetime: bookingData.return_datetime,
        vehicle_category: bookingData.vehicle_category,
        flight_number: bookingData.flight_number,
        train_number: bookingData.train_number
      });
    } else if (mode === 'passengers') {
      setEditData({
        passengers: bookingData.passengers,
        luggage: bookingData.luggage,
        options: bookingData.options || [],
        special_requests: bookingData.special_requests
      });
    } else if (mode === 'contact') {
      setEditData({
        client_name: bookingData.client_name,
        client_email: bookingData.client_email,
        client_phone: bookingData.client_phone
      });
    }
  };

  // Sauvegarder les modifications
  const saveEdit = async () => {
    if (editMode === 'trip' || editMode === 'passengers') {
      await recalculatePrice(editData);
    } else {
      updateBookingData(editData);
    }
    setEditMode(null);
    setEditData({});
  };

  // Annuler l'édition
  const cancelEdit = () => {
    setEditMode(null);
    setEditData({});
  };

  const handleSubmit = async () => {
    if (!cgvAccepted) {
      setError("Vous devez accepter les CGV pour valider votre réservation");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bookingPayload = {
        pickup: bookingData.pickup,
        dropoff: bookingData.dropoff,
        pickup_datetime: bookingData.pickup_datetime,
        is_round_trip: bookingData.is_round_trip,
        return_datetime: bookingData.return_datetime || null,
        intermediate_stops: bookingData.intermediate_stops || [],
        passengers: bookingData.passengers,
        luggage: bookingData.luggage,
        service_type: bookingData.service_type || 'transfer',
        hours_booked: bookingData.hours_booked,
        options: bookingData.options || [],
        special_requests: bookingData.special_requests || null,
        vehicle_category: bookingData.vehicle_category,
        client_email: bookingData.client_email || user?.email,
        client_name: bookingData.client_name || `${user?.first_name} ${user?.last_name}`,
        client_phone: bookingData.client_phone || user?.phone,
        cgv_accepted: true,
        price_details: bookingData.price_details,
        flight_number: bookingData.flight_number || null,
        departure_flight_number: bookingData.departure_flight_number || null,
        train_number: bookingData.train_number || null,
        departure_train_number: bookingData.departure_train_number || null,
        passenger_names: bookingData.passenger_names || [],
        payment_method: paymentMethod
      };

      const result = await bookingAPI.createBooking(bookingPayload);
      const confirmed = await bookingAPI.confirmBooking(result.id);
      
      const bookingId = confirmed.id || result.id;
      const bookingNumber = confirmed.booking_number || result.booking_number;
      
      updateBookingData({
        booking_id: bookingId,
        booking_number: bookingNumber,
        booking_status: 'pending_payment',
        payment_method: paymentMethod
      });
      
      // If card payment, redirect to Stripe
      if (paymentMethod === 'card') {
        try {
          const paymentResponse = await axios.post(`${API_URL}/api/payments/checkout`, {
            booking_id: bookingId,
            origin_url: window.location.origin
          });

          if (paymentResponse.data.checkout_url) {
            window.location.href = paymentResponse.data.checkout_url;
            return;
          } else {
            setError("URL de paiement non reçue");
            setLoading(false);
          }
        } catch (payErr) {
          console.error('Payment error:', payErr);
          setError(payErr.response?.data?.detail || "Erreur lors de l'initialisation du paiement.");
          setLoading(false);
        }
      } else {
        // Cash payment - update booking and redirect to success with booking_number
        try {
          await axios.put(`${API_URL}/api/bookings/${bookingId}/payment-method`, {
            payment_method: 'cash',
            payment_status: 'pending'
          });
          
          navigate(`/booking/payment/success?booking_id=${bookingId}&booking_number=${bookingNumber}&method=cash`);
        } catch (cashErr) {
          console.error('Cash update error:', cashErr);
          setError(cashErr.response?.data?.detail || "Erreur lors de l'enregistrement.");
          setLoading(false);
        }
      }
      
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.detail?.message || err.message || "Erreur lors de la création de la réservation");
      setLoading(false);
    }
  };

  const priceDetails = bookingData.price_details;
  const totalPrice = priceDetails?.total_ttc || bookingData.price_estimate?.total || 0;
  
  const hasFlightNumber = bookingData.flight_number;
  const hasTrainNumber = bookingData.train_number;

  // Composant d'édition inline pour Trajet
  const renderTripEdit = () => (
    <div className="space-y-4 animate-in fade-in-50">
      <div className="space-y-2">
        <Label className="text-[#E5E5E5] text-xs">Départ</Label>
        <GooglePlacesAutocomplete
          value={editData.pickup}
          onChange={(value) => setEditData({ ...editData, pickup: value })}
          placeholder="Adresse de départ"
          testId="edit-pickup"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#E5E5E5] text-xs">Arrivée</Label>
        <GooglePlacesAutocomplete
          value={editData.dropoff}
          onChange={(value) => setEditData({ ...editData, dropoff: value })}
          placeholder="Adresse d'arrivée"
          testId="edit-dropoff"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#E5E5E5] text-xs">Date et heure</Label>
        <Input
          type="datetime-local"
          value={editData.pickup_datetime}
          onChange={(e) => setEditData({ ...editData, pickup_datetime: e.target.value })}
          className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] h-9 text-sm"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#E5E5E5] text-xs">Véhicule</Label>
        <div className="grid grid-cols-2 gap-2">
          {vehicles.map(v => (
            <button
              key={v.category}
              onClick={() => setEditData({ ...editData, vehicle_category: v.category })}
              className={`p-2 rounded border text-xs ${
                editData.vehicle_category === v.category
                  ? 'border-[#C9A24D] bg-[#C9A24D]/10 text-[#C9A24D]'
                  : 'border-[#E5E5E5]/20 text-[#E5E5E5]/60 hover:border-[#C9A24D]/50'
              }`}
            >
              {v.name}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={saveEdit}
          disabled={recalculating}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] h-8"
        >
          {recalculating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Enregistrer
        </Button>
        <Button size="sm" variant="outline" onClick={cancelEdit} className="border-[#E5E5E5]/20 h-8">
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  // Composant d'édition inline pour Passagers
  const renderPassengersEdit = () => (
    <div className="space-y-4 animate-in fade-in-50">
      <div className="flex items-center justify-between">
        <Label className="text-[#E5E5E5] text-xs">Passagers</Label>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => setEditData({ ...editData, passengers: Math.max(1, (editData.passengers || 1) - 1) })}
            className="h-6 w-6 border-[#C9A24D]/30"
          >
            <Minus className="w-3 h-3" />
          </Button>
          <span className="text-[#E5E5E5] w-6 text-center">{editData.passengers}</span>
          <Button
            size="icon"
            variant="outline"
            onClick={() => setEditData({ ...editData, passengers: Math.min(8, (editData.passengers || 1) + 1) })}
            className="h-6 w-6 border-[#C9A24D]/30"
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-[#E5E5E5] text-xs">Bagages</Label>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => setEditData({ ...editData, luggage: Math.max(0, (editData.luggage || 0) - 1) })}
            className="h-6 w-6 border-[#C9A24D]/30"
          >
            <Minus className="w-3 h-3" />
          </Button>
          <span className="text-[#E5E5E5] w-6 text-center">{editData.luggage}</span>
          <Button
            size="icon"
            variant="outline"
            onClick={() => setEditData({ ...editData, luggage: Math.min(10, (editData.luggage || 0) + 1) })}
            className="h-6 w-6 border-[#C9A24D]/30"
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-[#E5E5E5] text-xs">Options</Label>
        <div className="space-y-1">
          {options.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={(editData.options || []).includes(opt.value)}
                onCheckedChange={(checked) => {
                  const current = editData.options || [];
                  setEditData({
                    ...editData,
                    options: checked 
                      ? [...current, opt.value]
                      : current.filter(o => o !== opt.value)
                  });
                }}
                className="h-4 w-4 border-[#C9A24D]"
              />
              <span className="text-[#E5E5E5]">{opt.name}</span>
              <span className="text-[#C9A24D]">+{opt.price}€</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={saveEdit}
          disabled={recalculating}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] h-8"
        >
          {recalculating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Enregistrer
        </Button>
        <Button size="sm" variant="outline" onClick={cancelEdit} className="border-[#E5E5E5]/20 h-8">
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  // Composant d'édition inline pour Contact
  const renderContactEdit = () => (
    <div className="space-y-3 animate-in fade-in-50">
      <div className="space-y-1">
        <Label className="text-[#E5E5E5] text-xs">Nom complet</Label>
        <Input
          value={editData.client_name || ''}
          onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
          className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] h-8 text-sm"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[#E5E5E5] text-xs">Email</Label>
        <Input
          type="email"
          value={editData.client_email || ''}
          onChange={(e) => setEditData({ ...editData, client_email: e.target.value })}
          className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] h-8 text-sm"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[#E5E5E5] text-xs">Téléphone</Label>
        <Input
          type="tel"
          value={editData.client_phone || ''}
          onChange={(e) => setEditData({ ...editData, client_phone: e.target.value })}
          className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] h-8 text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={saveEdit}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] h-8"
        >
          <Save className="w-3 h-3 mr-1" />
          Enregistrer
        </Button>
        <Button size="sm" variant="outline" onClick={cancelEdit} className="border-[#E5E5E5]/20 h-8">
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="booking-step-validation">
      <div className="text-center mb-4">
        <h2 className="text-xl font-['Playfair_Display'] text-[#E5E5E5] mb-1">
          Validation
        </h2>
        <p className="text-[#E5E5E5]/60 text-xs">
          Vérifiez les détails avant de passer au paiement
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Section Trajet */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardHeader className="pb-2 px-4 pt-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-[#C9A24D] text-xs font-medium flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Trajet
            </CardTitle>
            {editMode !== 'trip' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => startEdit('trip')}
                className="text-[#C9A24D] hover:text-[#E6C77A] hover:bg-[#C9A24D]/10 h-6 px-2 text-xs"
                data-testid="edit-trip"
              >
                <Pencil className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          {editMode === 'trip' ? renderTripEdit() : (
            <div className="space-y-2">
              <div className="grid grid-cols-1 gap-1 text-xs">
                {/* Départ */}
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-1" />
                  <span className="text-[#E5E5E5] flex-1 line-clamp-1">{bookingData.pickup?.address}</span>
                </div>
                
                {/* Étapes intermédiaires */}
                {bookingData.intermediate_stops && bookingData.intermediate_stops.length > 0 && (
                  <>
                    {bookingData.intermediate_stops.map((stop, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#C9A24D] mt-1" />
                        <span className="text-[#E5E5E5]/80 flex-1 line-clamp-1 text-xs">
                          {stop.address}
                        </span>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Arrivée */}
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-1" />
                  <span className="text-[#E5E5E5] flex-1 line-clamp-1">{bookingData.dropoff?.address}</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 text-xs text-[#E5E5E5]/70">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatShortDate(bookingData.pickup_datetime)}
                </span>
                {bookingData.is_round_trip && (
                  <span className="text-[#C9A24D]">A/R</span>
                )}
              </div>
              
              {(hasFlightNumber || hasTrainNumber) && (
                <div className="flex items-center gap-2 text-xs">
                  {hasFlightNumber && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded">
                      <Plane className="w-3 h-3" />
                      {bookingData.flight_number}
                    </span>
                  )}
                  {hasTrainNumber && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-500/10 text-orange-400 rounded">
                      <Train className="w-3 h-3" />
                      {bookingData.train_number}
                    </span>
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-between pt-1 border-t border-[#E5E5E5]/10">
                <div className="flex items-center gap-1">
                  <Car className="w-3 h-3 text-[#C9A24D]" />
                  <span className="text-[#E5E5E5] text-xs">{vehicleNames[bookingData.vehicle_category]}</span>
                </div>
                {priceDetails?.pricing_method?.selected_type?.toLowerCase().includes('zone') && (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <Tag className="w-3 h-3" />
                    Forfait
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Passagers */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardHeader className="pb-2 px-4 pt-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-[#C9A24D] text-xs font-medium flex items-center gap-1">
              <Users className="w-3 h-3" /> Passagers
            </CardTitle>
            {editMode !== 'passengers' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => startEdit('passengers')}
                className="text-[#C9A24D] hover:text-[#E6C77A] hover:bg-[#C9A24D]/10 h-6 px-2 text-xs"
                data-testid="edit-passengers"
              >
                <Pencil className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          {editMode === 'passengers' ? renderPassengersEdit() : (
            <div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-[#E5E5E5]">
                  <Users className="w-3 h-3 text-[#E5E5E5]/40" />
                  {bookingData.passengers} passager{bookingData.passengers > 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1 text-[#E5E5E5]">
                  <Briefcase className="w-3 h-3 text-[#E5E5E5]/40" />
                  {bookingData.luggage} bagage{bookingData.luggage > 1 ? 's' : ''}
                </span>
              </div>
              
              {(bookingData.options || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {bookingData.options.map(opt => (
                    <span key={opt} className="px-1.5 py-0.5 bg-[#C9A24D]/10 text-[#C9A24D] text-xs rounded">
                      {optionNames[opt] || opt}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Contact */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardHeader className="pb-2 px-4 pt-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-[#C9A24D] text-xs font-medium flex items-center gap-1">
              <User className="w-3 h-3" /> Contact
            </CardTitle>
            {editMode !== 'contact' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => startEdit('contact')}
                className="text-[#C9A24D] hover:text-[#E6C77A] hover:bg-[#C9A24D]/10 h-6 px-2 text-xs"
                data-testid="edit-identity"
              >
                <Pencil className="w-3 h-3 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          {editMode === 'contact' ? renderContactEdit() : (
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2 text-[#E5E5E5]">
                <User className="w-3 h-3 text-[#E5E5E5]/40" />
                {bookingData.client_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '-'}
              </div>
              <div className="flex items-center gap-2 text-[#E5E5E5]">
                <Mail className="w-3 h-3 text-[#E5E5E5]/40" />
                {bookingData.client_email || user?.email || '-'}
              </div>
              <div className="flex items-center gap-2 text-[#E5E5E5]">
                <Phone className="w-3 h-3 text-[#E5E5E5]/40" />
                {bookingData.client_phone || user?.phone || '-'}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prix Total */}
      <Card className="bg-gradient-to-br from-[#C9A24D]/20 to-[#1F1F1F] border-[#C9A24D]/30">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[#E5E5E5] text-sm">Total à payer</span>
              <p className="text-xs text-[#E5E5E5]/40">
                {priceDetails ? 'Prix définitif TTC' : 'Prix estimé TTC'}
              </p>
            </div>
            <div className="text-right">
              {recalculating ? (
                <Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" />
              ) : (
                <span className="text-2xl font-bold text-[#C9A24D]">
                  {Math.round(totalPrice)}€
                </span>
              )}
            </div>
          </div>
          
          {/* Surcharges Display */}
          {priceDetails?.surcharges && priceDetails.surcharges.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#C9A24D]/20" data-testid="confirmation-surcharges">
              <p className="text-xs text-[#E5E5E5]/60 mb-2">Suppléments inclus :</p>
              {priceDetails.surcharges.map((surcharge, index) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <span className="text-[#E5E5E5]/70 flex items-center gap-1">
                    {surcharge.type === 'night' && <span className="text-indigo-400">🌙</span>}
                    {surcharge.type === 'weekend' && <span className="text-orange-400">📅</span>}
                    {surcharge.type === 'holiday' && <span className="text-red-400">🎉</span>}
                    {surcharge.name}
                    {surcharge.rate && <span className="text-[#E5E5E5]/40">({surcharge.rate}%)</span>}
                  </span>
                  <span className="text-[#C9A24D]">+{surcharge.amount?.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mode de paiement */}
      <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardHeader className="pb-2 px-4 pt-3">
          <CardTitle className="text-[#C9A24D] text-xs font-medium flex items-center gap-1">
            <CreditCard className="w-3 h-3" /> Mode de paiement
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0 space-y-2">
          {/* Card Payment Option */}
          <button
            onClick={() => setPaymentMethod('card')}
            className={`w-full p-3 rounded-lg text-left transition-all border ${
              paymentMethod === 'card'
                ? 'bg-[#C9A24D]/10 border-[#C9A24D] ring-1 ring-[#C9A24D]/30'
                : 'bg-[#0B0B0B] border-[#C9A24D]/20 hover:border-[#C9A24D]/40'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${paymentMethod === 'card' ? 'bg-[#C9A24D]/20' : 'bg-[#1F1F1F]'}`}>
                <CreditCard className={`w-4 h-4 ${paymentMethod === 'card' ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/60'}`} />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${paymentMethod === 'card' ? 'text-[#C9A24D]' : 'text-[#E5E5E5]'}`}>
                  Paiement par carte
                </p>
                <p className="text-[10px] text-[#E5E5E5]/50">Paiement sécurisé via Stripe</p>
              </div>
              {paymentMethod === 'card' && <CheckCircle2 className="w-4 h-4 text-[#C9A24D]" />}
            </div>
          </button>

          {/* Cash Payment Option */}
          <button
            onClick={() => setPaymentMethod('cash')}
            className={`w-full p-3 rounded-lg text-left transition-all border ${
              paymentMethod === 'cash'
                ? 'bg-[#C9A24D]/10 border-[#C9A24D] ring-1 ring-[#C9A24D]/30'
                : 'bg-[#0B0B0B] border-[#C9A24D]/20 hover:border-[#C9A24D]/40'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${paymentMethod === 'cash' ? 'bg-[#C9A24D]/20' : 'bg-[#1F1F1F]'}`}>
                <Banknote className={`w-4 h-4 ${paymentMethod === 'cash' ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/60'}`} />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${paymentMethod === 'cash' ? 'text-[#C9A24D]' : 'text-[#E5E5E5]'}`}>
                  Paiement au chauffeur
                </p>
                <p className="text-[10px] text-[#E5E5E5]/50">Espèces ou carte à bord du véhicule</p>
              </div>
              {paymentMethod === 'cash' && <CheckCircle2 className="w-4 h-4 text-[#C9A24D]" />}
            </div>
          </button>
        </CardContent>
      </Card>

      {/* CGV Checkbox */}
      <div className="flex items-start gap-2 p-3 bg-[#1F1F1F] rounded-lg border border-[#C9A24D]/10">
        <Checkbox
          id="cgv"
          checked={cgvAccepted}
          onCheckedChange={setCgvAccepted}
          className="border-[#C9A24D] data-[state=checked]:bg-[#C9A24D] data-[state=checked]:text-[#0B0B0B] mt-0.5"
          data-testid="cgv-checkbox"
        />
        <label htmlFor="cgv" className="text-xs text-[#E5E5E5]/80 cursor-pointer">
          J&apos;accepte les{' '}
          <a href="/cgv" target="_blank" className="text-[#C9A24D] hover:underline">CGV</a>
          {' '}et la{' '}
          <a href="/privacy" target="_blank" className="text-[#C9A24D] hover:underline">politique de confidentialité</a>
        </label>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={loading || editMode !== null}
          className="flex-1 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 h-10"
          data-testid="step-validation-prev"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !cgvAccepted || editMode !== null}
          className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold disabled:opacity-50 h-10"
          data-testid="confirm-booking-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              {paymentMethod === 'card' ? 'Redirection...' : 'Confirmation...'}
            </>
          ) : paymentMethod === 'card' ? (
            <>
              <CreditCard className="w-4 h-4 mr-1" />
              Payer {Math.round(totalPrice)}€
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-1" />
              Confirmer la réservation
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Step6Confirmation;
